import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';
import { Icon, Sparkline, DonutChart, Legend, type IconName, type Slice } from './kycCharts';

/**
 * m.4.2 Hedging → Tržišni indikatori (uzroci naglih promjena cijene zlata).
 * Spec: KPI nacrt Hrvoja Gluvakovića (Monestiq), mail 478.
 *
 * v2 (Vedran, 07.09.2026): fullscreen preko cijelog ekrana (isti obrazac kao KYC modul 72 -
 * fixed overlay, bijeli header s pilulama), vizualni jezik i komponente iz kycCharts,
 * svi podaci live: FRED + CFTC + SPDR GLD arhiva + IMF COFER/IRFCL preko sync commanda,
 * gumb "Osvježi izvore" zove POST /hedge-market/sync. Ručni unos (superadmin) ostaje za
 * MOVE i IAU (nemaju besplatan feed) i kao override — ručni unos ima prednost nad feedom.
 */

type Locale = 'hr' | 'en';

type ManualRow = { metric_date: string; value: string | number; source: string | null; note?: string | null } | null;
type HistPoint = { metric_date: string; value: string | number; source: string | null };
type CftcRow = { report_date: string; long_position: number | string | null; short_position: number | string | null; net_position: number | string | null };
type Num = number | string | null;

type Summary = {
    as_of: string | null;
    bonds: {
        series: Array<{ date: string; dgs2: Num; dgs10: Num; dgs30: Num; dfii10: Num }>;
        latest: { dgs2: Num; dgs10: Num; dgs30: Num; dfii10: Num } | null;
        delta_30d_dfii10: number | null;
    };
    usd: {
        series: Array<{ date: string; dtwexbgs: Num }>;
        latest_dtwexbgs: Num;
        delta_30d: number | null;
        imf_reserve_share: { usd_pct: ManualRow; eur_pct: ManualRow; rmb_pct: ManualRow };
    };
    fed_liquidity: {
        series: Array<{ date: string; walcl: Num; rrp: Num; sofr: Num }>;
        latest: { walcl_musd: Num; rrpontsyd_busd: Num; sofr: Num } | null;
    };
    physical_paper: { comex_open_interest: ManualRow; gld_holdings_oz: ManualRow; iau_holdings_oz: ManualRow };
    credit_stress: {
        series: Array<{ date: string; hy_oas: Num; ig_oas: Num; ccc_oas: Num }>;
        latest: { hy_oas: Num; ig_oas: Num; ccc_oas: Num } | null;
        move_index: ManualRow;
        cftc: { gold: CftcRow[]; usd: CftcRow[]; treasury: CftcRow[]; equity: CftcRow[] };
    };
    global_transition: {
        imf_usd_share_pct: ManualRow;
        imf_eur_share_pct: ManualRow;
        imf_rmb_share_pct: ManualRow;
        china_gold_reserves_tonnes: ManualRow;
    };
    metric_history: Record<string, HistPoint[]>;
    manual_metrics: string[];
    can_write: boolean;
};

const INK = '#23405f';
const BTN: React.CSSProperties = { padding: '10px 16px', background: INK, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' };
const BTN2: React.CSSProperties = { ...BTN, background: '#e7edf3', color: INK };
const INP: React.CSSProperties = { padding: '10px 12px', border: '1px solid #ccd6e0', borderRadius: 8, fontSize: '0.95rem', boxSizing: 'border-box' };
const card: React.CSSProperties = { background: '#fff', border: '1px solid #d8e2ec', borderRadius: 12, padding: 16, marginBottom: 14 };

const nf = new Intl.NumberFormat('hr-HR');
const num = (v: Num): number | null => (v === null || v === undefined || v === '' ? null : Number(v));
const fmt = (v: Num, dec = 2): string => {
    const n = num(v);
    return n === null ? '—' : new Intl.NumberFormat('hr-HR', { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(n);
};
const fmtI = (v: Num): string => {
    const n = num(v);
    return n === null ? '—' : nf.format(Math.round(n));
};
const OZ_PER_TONNE = 32150.7466;

/** 'auto: ...' source = live feed; sve ostalo je covjekov unos. */
const isAuto = (r: ManualRow): boolean => !!r && typeof r.source === 'string' && r.source.startsWith('auto:');

function SourcePill({ row }: { row: ManualRow }) {
    if (!row) return <span style={{ fontSize: 10, fontWeight: 800, color: '#8b98a9', background: '#eef2f6', borderRadius: 6, padding: '2px 7px' }}>NEMA PODATKA</span>;
    const auto = isAuto(row);
    const c = auto ? '#1f7a36' : '#c2620a';
    return (
        <span title={`${row.source ?? ''} · ${row.metric_date}`} style={{ fontSize: 10, fontWeight: 800, color: c, background: `${c}18`, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>
            {auto ? 'AUTO' : 'RUČNO'} · {row.metric_date}
        </span>
    );
}

/** KPI kartica u KYC jeziku: ikona u toniranom kvadratu, velika brojka, delta, sparkline. */
function StatCard({ icon, label, value, sub, delta, deltaGoodDown, spark, color = INK, pill }: {
    icon: IconName; label: string; value: string; sub?: string;
    delta?: number | null; deltaGoodDown?: boolean; spark?: number[]; color?: string; pill?: React.ReactNode;
}) {
    const d = delta ?? null;
    const deltaColor = d === null ? '#8b98a9' : (d < 0 ? (deltaGoodDown ? '#1f7a36' : '#b11') : d > 0 ? (deltaGoodDown ? '#b11' : '#1f7a36') : '#8b98a9');
    return (
        <div className="hm-card" style={{ ...card, marginBottom: 0, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: `${color}14`, color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                    <Icon name={icon} size={18} />
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#5c6b7f', flex: 1, minWidth: 0 }}>{label}</span>
                {pill}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 25, fontWeight: 800, letterSpacing: '-.02em', color: '#1d2b3d', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
                {d !== null && (
                    <span style={{ fontSize: 12, fontWeight: 800, color: deltaColor, fontVariantNumeric: 'tabular-nums' }}>
                        {d > 0 ? '▲' : d < 0 ? '▼' : '='} {fmt(Math.abs(d), Math.abs(d) < 10 ? 2 : 0)}
                    </span>
                )}
            </div>
            {sub && <div style={{ fontSize: 11, color: '#8b98a9', marginTop: -4 }}>{sub}</div>}
            {spark && spark.length > 1 && <Sparkline values={spark} color={color} height={30} />}
        </div>
    );
}

/** Veci linijski graf za sekcije: area + min/max + rubni datumi. Ista svg tehnika kao Sparkline. */
function TrendChart({ points, color = INK, height = 130, dec = 2, unit = '' }: {
    points: Array<{ date: string; value: number }>; color?: string; height?: number; dec?: number; unit?: string;
}) {
    if (points.length < 2) return <div style={{ color: '#8b98a9', fontSize: 12.5, padding: '14px 0' }}>Nema dovoljno podataka.</div>;
    const vals = points.map((p) => p.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min || 1;
    const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * 100},${34 - ((v - min) / range) * 30}`).join(' ');
    const last = points[points.length - 1];
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8b98a9', fontWeight: 650, marginBottom: 2 }}>
                <span>max {fmt(max, dec)}{unit}</span>
                <span style={{ color, fontWeight: 800 }}>{fmt(last.value, dec)}{unit}</span>
            </div>
            <svg viewBox="0 0 100 36" preserveAspectRatio="none" style={{ width: '100%', height, display: 'block', overflow: 'visible' }} role="img" aria-hidden="true">
                <polygon points={`0,36 ${pts} 100,36`} fill={color} opacity={0.12} />
                <polyline points={pts} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
            </svg>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: '#8b98a9', marginTop: 4 }}>
                <span>{points[0].date}</span>
                <span>min {fmt(min, dec)}{unit}</span>
                <span>{last.date}</span>
            </div>
        </div>
    );
}

/** Stupci oko nulte linije za CFTC net pozicije (net moze biti negativan, ColumnChart to ne zna). */
function NetBarChart({ rows, height = 130 }: { rows: CftcRow[]; height?: number }) {
    const data = rows.map((r) => ({ label: r.report_date.slice(5), net: num(r.net_position) ?? 0 }));
    if (data.length === 0) return <div style={{ color: '#8b98a9', fontSize: 12.5, padding: '14px 0' }}>Nema podataka.</div>;
    const maxAbs = Math.max(1, ...data.map((d) => Math.abs(d.net)));
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, height }}>
                {data.map((d, i) => {
                    const h = (Math.abs(d.net) / maxAbs) * 46;
                    const up = d.net >= 0;
                    return (
                        <div key={i} title={`${d.label}: ${fmtI(d.net)}`} style={{ flex: '1 1 0', height: '100%', position: 'relative', minWidth: 0 }}>
                            <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px solid #dfe4ea' }} />
                            <div style={{
                                position: 'absolute', left: '12%', right: '12%',
                                [up ? 'bottom' : 'top']: '50%', height: `${Math.max(h, 1.5)}%`,
                                background: up ? '#1f7a36' : '#b11', borderRadius: 3,
                            } as React.CSSProperties} />
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                {data.map((d, i) => <div key={i} style={{ flex: '1 1 0', fontSize: 9, color: '#8b98a9', textAlign: 'center', fontWeight: 650, whiteSpace: 'nowrap', overflow: 'hidden' }}>{d.label}</div>)}
            </div>
            <div style={{ fontSize: 11, color: '#7b8899', fontWeight: 600, marginTop: 6 }}>
                Net pozicija velikih spekulanata (long − short), ugovori. Zadnje: <b style={{ color: data[data.length - 1].net >= 0 ? '#1f7a36' : '#b11' }}>{fmtI(data[data.length - 1].net)}</b>
            </div>
        </div>
    );
}

/** Naslov sekcijske kartice s ikonom. */
function CardTitle({ icon, children }: { icon: IconName; children: React.ReactNode }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ color: INK, display: 'inline-flex' }}><Icon name={icon} size={17} /></span>
            <strong style={{ fontSize: 14.5, color: INK }}>{children}</strong>
        </div>
    );
}

/** Definicije metrika za rucni unos / override (superadmin). */
const METRIC_DEFS: Array<{ key: string; label: string; unit: string; auto: boolean }> = [
    { key: 'move_index', label: 'MOVE index (volatilnost obveznica)', unit: 'bp', auto: false },
    { key: 'iau_holdings_oz', label: 'IAU holdings (iShares Gold Trust)', unit: 'oz', auto: false },
    { key: 'comex_open_interest', label: 'COMEX open interest (zlato)', unit: 'ugovora', auto: true },
    { key: 'gld_holdings_oz', label: 'GLD holdings (SPDR Gold Shares)', unit: 'oz', auto: true },
    { key: 'imf_usd_share_pct', label: 'IMF COFER — udio USD', unit: '%', auto: true },
    { key: 'imf_eur_share_pct', label: 'IMF COFER — udio EUR', unit: '%', auto: true },
    { key: 'imf_rmb_share_pct', label: 'IMF COFER — udio RMB', unit: '%', auto: true },
    { key: 'china_gold_reserves_tonnes', label: 'Kina — rezerve zlata', unit: 't', auto: true },
];

/** Redak rucnog unosa: zadnja vrijednost + forma datum/vrijednost/izvor. Rucni unos pregazuje auto za isti datum. */
function ManualEntryRow({ def, latest, token, onSaved }: {
    def: (typeof METRIC_DEFS)[number]; latest: ManualRow; token: string | null; onSaved: () => void;
}) {
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [value, setValue] = useState('');
    const [source, setSource] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const save = async () => {
        if (!value.trim()) { setMsg('Upiši vrijednost.'); return; }
        setBusy(true); setMsg('');
        try {
            await mutate(`/hedge-market/manual`, token, 'POST', {
                metric_key: def.key, metric_date: date, value: Number(value.replace(',', '.')), source: source || null,
            });
            setValue(''); setSource(''); setMsg('Spremljeno.');
            onSaved();
        } catch (e) {
            setMsg(e instanceof Error ? e.message : String(e));
        } finally { setBusy(false); }
    };
    return (
        <div className="hm-card" style={{ ...card, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <strong style={{ fontSize: 13.5, color: INK, flex: 1, minWidth: 220 }}>{def.label}</strong>
                <SourcePill row={latest} />
                <span style={{ fontSize: 14, fontWeight: 800, color: '#1d2b3d', fontVariantNumeric: 'tabular-nums' }}>
                    {latest ? `${fmt(latest.value, def.unit === '%' ? 2 : 0)} ${def.unit}` : '—'}
                </span>
            </div>
            {def.auto && <div style={{ fontSize: 11, color: '#8b98a9', marginBottom: 8 }}>Puni se automatski; ručni unos za isti datum ima prednost nad feedom.</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...INP, width: 150 }} aria-label="Datum" />
                <input type="text" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder={`Vrijednost (${def.unit})`} style={{ ...INP, width: 160 }} />
                <input type="text" value={source} onChange={(e) => setSource(e.target.value)} placeholder="Izvor (npr. ICE, iShares…)" style={{ ...INP, flex: '1 1 180px' }} />
                <button type="button" onClick={() => void save()} disabled={busy} style={BTN}>{busy ? '…' : 'Spremi'}</button>
                {msg && <span style={{ fontSize: 12, color: msg === 'Spremljeno.' ? '#1f7a36' : '#b11', fontWeight: 700 }}>{msg}</span>}
            </div>
        </div>
    );
}

type View = 'pregled' | 'bonds' | 'usd' | 'fed' | 'physical' | 'credit' | 'global' | 'unos';

export function HedgingMarketDashboardModule({ token, locale = 'hr', onExit }: { token: string | null; locale?: Locale; onExit?: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Summary | null>(null);
    const [days, setDays] = useState(180);
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    const [syncBusy, setSyncBusy] = useState(false);
    const [syncMsg, setSyncMsg] = useState('');
    const [view, setView] = useState<View>('pregled');

    const load = useCallback(async () => {
        setBusy(true); setErr('');
        try {
            setData(await api<Summary>(`/hedge-market/summary?days=${days}`, token));
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
        } finally { setBusy(false); }
    }, [days, token]);
    useEffect(() => { void load(); }, [load]);

    const doSync = async () => {
        setSyncBusy(true); setSyncMsg('');
        try {
            await mutate<{ ok: boolean }>(`/hedge-market/sync`, token, 'POST');
            setSyncMsg(t('Izvori osvježeni.', 'Sources refreshed.'));
            await load();
        } catch (e) {
            setSyncMsg(e instanceof Error ? e.message : String(e));
        } finally { setSyncBusy(false); }
    };

    const hist = useCallback((key: string): number[] => {
        const rows = data?.metric_history?.[key] ?? [];
        return rows.map((r) => num(r.value) ?? 0);
    }, [data]);
    const histPoints = useCallback((key: string): Array<{ date: string; value: number }> => {
        const rows = data?.metric_history?.[key] ?? [];
        return rows.map((r) => ({ date: r.metric_date, value: num(r.value) ?? 0 }));
    }, [data]);

    const series = useCallback(<T extends { date: string }>(rows: T[] | undefined, pick: (r: T) => Num): Array<{ date: string; value: number }> => {
        return (rows ?? []).map((r) => ({ date: r.date, value: num(pick(r)) })).filter((p): p is { date: string; value: number } => p.value !== null);
    }, []);

    const coferSlices = useMemo((): Slice[] => {
        if (!data) return [];
        const usd = num(data.global_transition.imf_usd_share_pct?.value ?? null);
        const eur = num(data.global_transition.imf_eur_share_pct?.value ?? null);
        const rmb = num(data.global_transition.imf_rmb_share_pct?.value ?? null);
        if (usd === null) return [];
        const rest = Math.max(0, 100 - (usd ?? 0) - (eur ?? 0) - (rmb ?? 0));
        return [
            { key: 'usd', label: 'USD', value: Math.round(usd * 10) / 10, color: INK },
            { key: 'eur', label: 'EUR', value: Math.round((eur ?? 0) * 10) / 10, color: '#5b7a9a' },
            { key: 'rmb', label: 'RMB', value: Math.round((rmb ?? 0) * 10) / 10, color: '#b1611e' },
            { key: 'rest', label: t('Ostale', 'Other'), value: Math.round(rest * 10) / 10, color: '#c9d4de' },
        ];
    }, [data, t]);

    const tabs: Array<[View, string]> = [
        ['pregled', t('Pregled', 'Overview')],
        ['bonds', t('Obveznice', 'Bonds')],
        ['usd', 'USD'],
        ['fed', t('FED likvidnost', 'Fed liquidity')],
        ['physical', t('Fizičko vs papirnato', 'Physical vs paper')],
        ['credit', t('Kreditni stres', 'Credit stress')],
        ['global', t('Globalna tranzicija', 'Global transition')],
        ...(data?.can_write ? [['unos', t('Ručni unos', 'Manual entry')] as [View, string]] : []),
    ];

    const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 };
    const grid2: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 };

    return (
        <div className="hm-shell" style={{ position: 'fixed', inset: 0, zIndex: 4000, background: '#f3f5f8', color: INK, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <style>{`
                .hm-shell { font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-size: 14px; line-height: 1.45; }
                .hm-shell button, .hm-shell input, .hm-shell select { font: inherit; }
                .hm-shell button:focus-visible, .hm-shell input:focus-visible, .hm-shell select:focus-visible { outline: 3px solid rgba(35,64,95,.24); outline-offset: 2px; }
                .hm-nav button.active { background: ${INK} !important; border-color: ${INK} !important; color: #fff !important; box-shadow: 0 4px 12px rgba(35,64,95,.18); }
                .hm-card { transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease; }
                .hm-card:hover { transform: translateY(-2px); border-color: #b8c1ce !important; box-shadow: 0 12px 28px rgba(23,32,51,.12); }
                @media (max-width: 760px) { .hm-main { padding: 14px !important; } }
                @media (prefers-reduced-motion: reduce) { .hm-card { transition: none !important; } .hm-card:hover { transform: none; } }
            `}</style>
            <div style={{ background: '#fff', borderBottom: '1px solid #dfe4ea', padding: '18px 24px 0', boxShadow: '0 2px 10px rgba(23,32,51,.05)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', paddingBottom: 14 }}>
                    <div>
                        <div style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: '#9b6b16', fontWeight: 750 }}>Auro Domus · Modul 4.2 · Hedging</div>
                        <div style={{ fontSize: 27, fontWeight: 750, letterSpacing: '-.025em', lineHeight: 1.15, marginTop: 2 }}>{t('Tržišni indikatori', 'Market indicators')}</div>
                        <div style={{ fontSize: 11.5, color: '#8b98a9', marginTop: 2 }}>{t('Stanje na dan', 'As of')}: {data?.as_of ?? '—'}{busy ? ' · učitavam…' : ''}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select aria-label={t('Raspon', 'Range')} value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ ...INP, padding: '8px 10px', width: 'auto' }}>
                            <option value={90}>90 {t('dana', 'days')}</option>
                            <option value={180}>180 {t('dana', 'days')}</option>
                            <option value={365}>365 {t('dana', 'days')}</option>
                            <option value={1095}>3 {t('godine', 'years')}</option>
                        </select>
                        <button type="button" style={BTN} onClick={() => void doSync()} disabled={syncBusy}>{syncBusy ? t('Osvježavam…', 'Refreshing…') : `⟳ ${t('Osvježi izvore', 'Refresh sources')}`}</button>
                        {onExit && <button type="button" style={BTN2} onClick={onExit}>{t('Izlaz', 'Exit')}</button>}
                    </div>
                </div>
                <div className="hm-nav" style={{ display: 'flex', gap: 7, flexWrap: 'wrap', paddingBottom: 14 }}>
                    {tabs.map(([key, label]) => (
                        <button key={key} type="button" className={view === key ? 'active' : ''} onClick={() => setView(key)}
                            style={{ background: '#fff', border: '1px solid #d5dbe3', cursor: 'pointer', padding: '8px 14px', borderRadius: 9, fontWeight: 650, color: '#445066' }}>{label}</button>
                    ))}
                </div>
            </div>
            <div className="hm-main" style={{ flex: 1, overflowY: 'auto', padding: 24, boxSizing: 'border-box' }}>
                <section style={{ maxWidth: view === 'pregled' ? 1440 : 1180, margin: '0 auto' }}>
                    {err && <div style={{ ...card, borderColor: '#b11', color: '#b11', fontWeight: 700 }}>{err}</div>}
                    {syncMsg && <div style={{ ...card, padding: 10, color: syncMsg.includes('osvjež') || syncMsg.includes('refresh') ? '#1f7a36' : '#b11', fontWeight: 700 }}>{syncMsg}</div>}
                    {!data && !err && <div style={{ color: '#667', textAlign: 'center', padding: 24 }}>{t('Učitavam…', 'Loading…')}</div>}
                    {data && <ViewBody view={view} data={data} t={t} hist={hist} histPoints={histPoints} series={series} coferSlices={coferSlices} grid={grid} grid2={grid2} token={token} onSaved={() => void load()} />}
                </section>
            </div>
        </div>
    );
}

function ViewBody({ view, data, t, hist, histPoints, series, coferSlices, grid, grid2, token, onSaved }: {
    view: View; data: Summary; t: (hr: string, en: string) => string;
    hist: (key: string) => number[];
    histPoints: (key: string) => Array<{ date: string; value: number }>;
    series: <T extends { date: string }>(rows: T[] | undefined, pick: (r: T) => Num) => Array<{ date: string; value: number }>;
    coferSlices: Slice[]; grid: React.CSSProperties; grid2: React.CSSProperties;
    token: string | null; onSaved: () => void;
}) {
    const b = data.bonds;
    const u = data.usd;
    const f = data.fed_liquidity;
    const p = data.physical_paper;
    const c = data.credit_stress;
    const g = data.global_transition;
    const walclT = num(f.latest?.walcl_musd ?? null);
    const gldOz = num(p.gld_holdings_oz?.value ?? null);
    const chinaT = num(g.china_gold_reserves_tonnes?.value ?? null);
    const usdShare = num(g.imf_usd_share_pct?.value ?? null);

    if (view === 'pregled') {
        return (
            <>
                <div style={grid}>
                    <StatCard icon="trend" label={t('10Y realni prinos (TIPS)', '10Y real yield (TIPS)')} value={`${fmt(b.latest?.dfii10 ?? null)} %`}
                        delta={b.delta_30d_dfii10} deltaGoodDown sub={t('Δ 30 dana; pad realnog prinosa podržava zlato', '30d Δ; falling real yield supports gold')}
                        spark={series(b.series, (r) => r.dfii10).map((x) => x.value)} />
                    <StatCard icon="cash" label={t('USD indeks (broad)', 'USD index (broad)')} value={fmt(u.latest_dtwexbgs)}
                        delta={u.delta_30d} deltaGoodDown sub={t('Δ 30 dana; slabiji USD podržava zlato', '30d Δ; weaker USD supports gold')}
                        spark={series(u.series, (r) => r.dtwexbgs).map((x) => x.value)} />
                    <StatCard icon="scale" label={t('FED bilanca', 'Fed balance sheet')} value={walclT === null ? '—' : `${fmt(walclT / 1e6, 2)} bil. USD`}
                        sub={`RRP ${fmt(f.latest?.rrpontsyd_busd ?? null, 0)} mlrd · SOFR ${fmt(f.latest?.sofr ?? null)} %`}
                        spark={series(f.series, (r) => r.walcl).map((x) => x.value)} />
                    <StatCard icon="bolt" label={t('HY kreditni spread (OAS)', 'HY credit spread (OAS)')} value={`${fmt(c.latest?.hy_oas ?? null)} %`}
                        sub={`IG ${fmt(c.latest?.ig_oas ?? null)} % · CCC ${fmt(c.latest?.ccc_oas ?? null)} %`}
                        spark={series(c.series, (r) => r.hy_oas).map((x) => x.value)} color="#b1611e" />
                    <StatCard icon="folder" label="COMEX open interest" value={fmtI(p.comex_open_interest?.value ?? null)}
                        sub={t('ugovora (zlato)', 'contracts (gold)')} pill={<SourcePill row={p.comex_open_interest} />}
                        spark={hist('comex_open_interest')} />
                    <StatCard icon="lock" label="GLD holdings" value={gldOz === null ? '—' : `${fmt(gldOz / OZ_PER_TONNE, 1)} t`}
                        sub={gldOz === null ? undefined : `${fmtI(gldOz)} oz`} pill={<SourcePill row={p.gld_holdings_oz} />}
                        spark={hist('gld_holdings_oz')} />
                    <StatCard icon="flag" label={t('Udio USD u svjetskim rezervama', 'USD share of world reserves')} value={usdShare === null ? '—' : `${fmt(usdShare, 1)} %`}
                        sub="IMF COFER" pill={<SourcePill row={g.imf_usd_share_pct} />} spark={hist('imf_usd_share_pct')} />
                    <StatCard icon="shield" label={t('Kina — rezerve zlata', 'China gold reserves')} value={chinaT === null ? '—' : `${fmt(chinaT, 0)} t`}
                        sub="IMF IRFCL" pill={<SourcePill row={g.china_gold_reserves_tonnes} />} spark={hist('china_gold_reserves_tonnes')} color="#b1611e" />
                </div>
                <div style={{ ...grid2, marginTop: 14 }}>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="trend">{t('10Y realni prinos', '10Y real yield')}</CardTitle>
                        <TrendChart points={series(b.series, (r) => r.dfii10)} unit=" %" />
                    </div>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="folder">{t('CFTC net pozicije — zlato', 'CFTC net positions — gold')}</CardTitle>
                        <NetBarChart rows={c.cftc.gold} />
                    </div>
                </div>
            </>
        );
    }

    if (view === 'bonds') {
        return (
            <>
                <div style={grid}>
                    <StatCard icon="trend" label="2Y UST" value={`${fmt(b.latest?.dgs2 ?? null)} %`} spark={series(b.series, (r) => r.dgs2).map((x) => x.value)} />
                    <StatCard icon="trend" label="10Y UST" value={`${fmt(b.latest?.dgs10 ?? null)} %`} spark={series(b.series, (r) => r.dgs10).map((x) => x.value)} />
                    <StatCard icon="trend" label="30Y UST" value={`${fmt(b.latest?.dgs30 ?? null)} %`} spark={series(b.series, (r) => r.dgs30).map((x) => x.value)} />
                    <StatCard icon="trend" label={t('10Y realni (TIPS)', '10Y real (TIPS)')} value={`${fmt(b.latest?.dfii10 ?? null)} %`}
                        delta={b.delta_30d_dfii10} deltaGoodDown spark={series(b.series, (r) => r.dfii10).map((x) => x.value)} color="#b1611e" />
                </div>
                <div style={{ ...grid2, marginTop: 14 }}>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="trend">{t('10Y nominalni prinos', '10Y nominal yield')}</CardTitle>
                        <TrendChart points={series(b.series, (r) => r.dgs10)} unit=" %" />
                    </div>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="trend">{t('10Y realni prinos (TIPS)', '10Y real yield (TIPS)')}</CardTitle>
                        <TrendChart points={series(b.series, (r) => r.dfii10)} unit=" %" color="#b1611e" />
                    </div>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="scale">{t('Krivulja: 10Y − 2Y', 'Curve: 10Y − 2Y')}</CardTitle>
                        <TrendChart points={(b.series ?? []).map((r) => {
                            const a = num(r.dgs10); const z = num(r.dgs2);
                            return a !== null && z !== null ? { date: r.date, value: Math.round((a - z) * 100) / 100 } : null;
                        }).filter((x): x is { date: string; value: number } => x !== null)} unit=" pp" color="#5b7a9a" />
                    </div>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="folder">{t('CFTC net pozicije — 10Y treasury', 'CFTC net — 10Y treasury')}</CardTitle>
                        <NetBarChart rows={c.cftc.treasury} />
                    </div>
                </div>
                <div style={{ ...card, marginTop: 14, fontSize: 12, color: '#7b8899' }}>
                    {t('Nagli rast realnog prinosa tipično pritišće zlato; pad ga podržava. Izvor: FRED (DGS2/DGS10/DGS30/DFII10), dnevno.',
                        'A sharp rise in real yield typically pressures gold; a fall supports it. Source: FRED (DGS2/DGS10/DGS30/DFII10), daily.')}
                </div>
            </>
        );
    }

    if (view === 'usd') {
        return (
            <div style={grid2}>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="cash">{t('USD indeks (trade-weighted, broad)', 'USD index (trade-weighted, broad)')}</CardTitle>
                    <TrendChart points={series(u.series, (r) => r.dtwexbgs)} dec={1} />
                    <div style={{ fontSize: 12, color: '#7b8899', marginTop: 8 }}>
                        {t('Δ 30 dana:', '30d Δ:')} <b style={{ color: (u.delta_30d ?? 0) <= 0 ? '#1f7a36' : '#b11' }}>{fmt(u.delta_30d)}</b> · {t('slabiji USD u pravilu podiže zlato', 'weaker USD generally lifts gold')}
                    </div>
                </div>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="folder">{t('CFTC net pozicije — USD indeks', 'CFTC net — USD index')}</CardTitle>
                    <NetBarChart rows={c.cftc.usd} />
                </div>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="flag">{t('Sastav svjetskih deviznih rezervi (IMF COFER)', 'World FX reserve composition (IMF COFER)')}</CardTitle>
                    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                        <DonutChart slices={coferSlices} centerValue={usdShare === null ? '—' : `${fmt(usdShare, 1)}%`} centerLabel="USD" />
                        <div style={{ flex: 1, minWidth: 180 }}><Legend slices={coferSlices} /></div>
                    </div>
                    <div style={{ marginTop: 8 }}><SourcePill row={g.imf_usd_share_pct} /></div>
                </div>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="trend">{t('Udio USD kroz vrijeme', 'USD share over time')}</CardTitle>
                    <TrendChart points={histPoints('imf_usd_share_pct')} unit=" %" dec={1} />
                </div>
            </div>
        );
    }

    if (view === 'fed') {
        return (
            <div style={grid2}>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="scale">{t('FED bilanca (WALCL)', 'Fed balance sheet (WALCL)')}</CardTitle>
                    <TrendChart points={series(f.series, (r) => r.walcl).map((x) => ({ date: x.date, value: Math.round((x.value / 1e6) * 100) / 100 }))} unit=" bil." />
                    <div style={{ fontSize: 12, color: '#7b8899', marginTop: 8 }}>{t('Tjedna serija, bilijuni USD. Širenje bilance = više likvidnosti = potpora zlatu.', 'Weekly series, USD trillions. Balance sheet expansion = more liquidity = gold support.')}</div>
                </div>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="cash">{t('Overnight reverse repo (RRP)', 'Overnight reverse repo (RRP)')}</CardTitle>
                    <TrendChart points={series(f.series, (r) => r.rrp)} unit=" mlrd" dec={0} color="#5b7a9a" />
                </div>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="clock">SOFR</CardTitle>
                    <TrendChart points={series(f.series, (r) => r.sofr)} unit=" %" color="#b1611e" />
                    <div style={{ fontSize: 12, color: '#7b8899', marginTop: 8 }}>{t('Skok SOFR-a iznad IORB-a signalizira stres na repo tržištu (rujan 2019).', 'SOFR spiking above IORB signals repo market stress (September 2019).')}</div>
                </div>
            </div>
        );
    }

    if (view === 'physical') {
        return (
            <>
                <div style={grid}>
                    <StatCard icon="folder" label="COMEX open interest" value={fmtI(p.comex_open_interest?.value ?? null)} sub={t('ugovora (100 oz po ugovoru)', 'contracts (100 oz each)')}
                        pill={<SourcePill row={p.comex_open_interest} />} spark={hist('comex_open_interest')} />
                    <StatCard icon="lock" label="GLD (SPDR)" value={gldOz === null ? '—' : `${fmt(gldOz / OZ_PER_TONNE, 1)} t`} sub={gldOz === null ? undefined : `${fmtI(gldOz)} oz`}
                        pill={<SourcePill row={p.gld_holdings_oz} />} spark={hist('gld_holdings_oz')} color="#b1611e" />
                    <StatCard icon="lock" label="IAU (iShares)" value={p.iau_holdings_oz ? `${fmtI(p.iau_holdings_oz.value)} oz` : '—'}
                        pill={<SourcePill row={p.iau_holdings_oz} />} spark={hist('iau_holdings_oz')} />
                </div>
                <div style={{ ...grid2, marginTop: 14 }}>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="lock">{t('GLD — zlato u trustu (oz)', 'GLD — gold in trust (oz)')}</CardTitle>
                        <TrendChart points={histPoints('gld_holdings_oz')} dec={0} color="#b1611e" />
                    </div>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="folder">{t('COMEX open interest kroz tjedne', 'COMEX open interest, weekly')}</CardTitle>
                        <TrendChart points={histPoints('comex_open_interest')} dec={0} />
                    </div>
                </div>
                <div style={{ ...card, marginTop: 14, fontSize: 12, color: '#7b8899' }}>
                    {t('Rast ETF holdinga = priljev u „papirnato" zlato; nagli odljev uz stabilnu cijenu upućuje na fizičku potražnju drugdje (npr. Azija). IAU zahtijeva ručni unos — iShares blokira automatski dohvat.',
                        'Rising ETF holdings = inflows into "paper" gold; sharp outflows with stable price point to physical demand elsewhere (e.g. Asia). IAU requires manual entry — iShares blocks automated fetch.')}
                </div>
            </>
        );
    }

    if (view === 'credit') {
        return (
            <>
                <div style={grid}>
                    <StatCard icon="bolt" label="HY OAS" value={`${fmt(c.latest?.hy_oas ?? null)} %`} spark={series(c.series, (r) => r.hy_oas).map((x) => x.value)} color="#b1611e" />
                    <StatCard icon="bolt" label="IG OAS" value={`${fmt(c.latest?.ig_oas ?? null)} %`} spark={series(c.series, (r) => r.ig_oas).map((x) => x.value)} />
                    <StatCard icon="alert" label="CCC OAS" value={`${fmt(c.latest?.ccc_oas ?? null)} %`} spark={series(c.series, (r) => r.ccc_oas).map((x) => x.value)} color="#b11" />
                    <StatCard icon="alert" label="MOVE index" value={c.move_index ? fmt(c.move_index.value, 0) : '—'} sub={t('volatilnost obveznica', 'bond volatility')}
                        pill={<SourcePill row={c.move_index} />} spark={hist('move_index')} />
                </div>
                <div style={{ ...grid2, marginTop: 14 }}>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="bolt">{t('HY spread (OAS)', 'HY spread (OAS)')}</CardTitle>
                        <TrendChart points={series(c.series, (r) => r.hy_oas)} unit=" %" color="#b1611e" />
                    </div>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="folder">{t('CFTC net — zlato', 'CFTC net — gold')}</CardTitle>
                        <NetBarChart rows={c.cftc.gold} />
                    </div>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="folder">{t('CFTC net — E-mini S&P 500', 'CFTC net — E-mini S&P 500')}</CardTitle>
                        <NetBarChart rows={c.cftc.equity} />
                    </div>
                    <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                        <CardTitle icon="folder">{t('CFTC net — USD indeks', 'CFTC net — USD index')}</CardTitle>
                        <NetBarChart rows={c.cftc.usd} />
                    </div>
                </div>
            </>
        );
    }

    if (view === 'global') {
        return (
            <div style={grid2}>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="flag">{t('IMF COFER — sastav rezervi', 'IMF COFER — reserve composition')}</CardTitle>
                    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                        <DonutChart slices={coferSlices} centerValue={usdShare === null ? '—' : `${fmt(usdShare, 1)}%`} centerLabel="USD" />
                        <div style={{ flex: 1, minWidth: 180 }}><Legend slices={coferSlices} /></div>
                    </div>
                    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <SourcePill row={g.imf_usd_share_pct} />
                    </div>
                </div>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="trend">{t('Udio USD kroz kvartale', 'USD share by quarter')}</CardTitle>
                    <TrendChart points={histPoints('imf_usd_share_pct')} unit=" %" dec={1} />
                </div>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="trend">{t('Udio RMB kroz kvartale', 'RMB share by quarter')}</CardTitle>
                    <TrendChart points={histPoints('imf_rmb_share_pct')} unit=" %" dec={2} color="#b1611e" />
                </div>
                <div className="hm-card" style={{ ...card, marginBottom: 0 }}>
                    <CardTitle icon="shield">{t('Kina — službene rezerve zlata', 'China — official gold reserves')}</CardTitle>
                    <TrendChart points={histPoints('china_gold_reserves_tonnes')} unit=" t" dec={0} color="#b1611e" />
                    <div style={{ marginTop: 8 }}><SourcePill row={g.china_gold_reserves_tonnes} /></div>
                    <div style={{ fontSize: 12, color: '#7b8899', marginTop: 8 }}>
                        {t('Službene brojke (IMF IRFCL, mjesečno); stvarne kineske kupnje se procjenjuju znatno višima.',
                            'Official figures (IMF IRFCL, monthly); actual Chinese purchases are widely estimated higher.')}
                    </div>
                </div>
            </div>
        );
    }

    // view === 'unos'
    return (
        <>
            <div style={{ ...card, background: '#f7fafc', fontSize: 12.5, color: '#5c6b7f' }}>
                {t('Ručni unos je override: vrijednost za isti datum ima prednost nad automatskim feedom i feed je više ne dira. MOVE i IAU nemaju besplatan feed pa se vode isključivo ručno.',
                    'Manual entry is an override: a value for the same date takes precedence over the automatic feed. MOVE and IAU have no free feed and are maintained manually.')}
            </div>
            {METRIC_DEFS.map((def) => {
                const latest: ManualRow = def.key === 'move_index' ? c.move_index
                    : def.key === 'iau_holdings_oz' ? p.iau_holdings_oz
                        : def.key === 'comex_open_interest' ? p.comex_open_interest
                            : def.key === 'gld_holdings_oz' ? p.gld_holdings_oz
                                : def.key === 'imf_usd_share_pct' ? g.imf_usd_share_pct
                                    : def.key === 'imf_eur_share_pct' ? g.imf_eur_share_pct
                                        : def.key === 'imf_rmb_share_pct' ? g.imf_rmb_share_pct
                                            : g.china_gold_reserves_tonnes;
                return <ManualEntryRow key={def.key} def={def} latest={latest} token={token} onSaved={onSaved} />;
            })}
        </>
    );
}

export default HedgingMarketDashboardModule;

